// Inline images in message bodies use markdown image syntax pointed at our
// own attachment endpoint: ![name](/api/attachments/file/…). Only that exact
// shape ever renders as an image — arbitrary or external URLs stay literal
// text, so ticket threads can't embed tracking pixels or off-site content.
//
// The referenced file is still a real attachment (uploaded through the normal
// two-step flow and sent in the message's attachments array) — that DB row is
// what drives per-ticket access control on the file route.

// URL part stops at whitespace/parens; our paths are URL-encoded so neither
// appears in a real attachment URL.
const INLINE_IMAGE_RE = /!\[([^\]\n]*)\]\((\/api\/attachments\/file\/[^\s()]+)\)/g;

// While a pasted image uploads, the compose box holds a placeholder whose
// target is not a URL — it can never render as an image if sent verbatim.
const PLACEHOLDER_RE = /!\[[^\]\n]*\]\(uploading:[\w-]+\)/;

/** Filenames go inside ![…] — strip the characters that would break it. */
function safeAlt(filename: string): string {
  return filename.replace(/[[\]()\n\r]/g, '_');
}

export function uploadingPlaceholder(id: string, filename: string): string {
  return `![Uploading ${safeAlt(filename)}…](uploading:${id})`;
}

export function inlineImageMarkdown(att: { filename: string; url: string }): string {
  return `![${safeAlt(att.filename)}](${att.url})`;
}

/** True while any pasted image is still uploading — block send until done. */
export function hasPendingInlineUploads(text: string): boolean {
  return PLACEHOLDER_RE.test(text);
}

// The insert/resolve helpers below mutate the textarea DOM (setRangeText) and
// return the new value for state sync, instead of going through setState and
// letting React rewrite the whole value. React restores the caret to its old
// NUMERIC offset after a controlled rewrite — and the final ![name](url)
// markdown is ~60 chars longer than the placeholder, so a caret sitting after
// the placeholder landed inside the swapped-in URL, and anything the user
// typed mid-upload spliced into the reference and broke it. Mutating the DOM
// moves text and caret atomically; the controlled re-render then sees an
// equal value and leaves the selection alone.

/** Insert the uploading placeholder at the caret (on its own line) and move
 *  the caret past it, so typing during the upload lands below the image.
 *  Falls back to appending to `value` when no element is available. */
export function insertUploadingPlaceholder(
  el: HTMLTextAreaElement | null,
  value: string,
  id: string,
  filename: string,
): string {
  const text = el ? el.value : value;
  const ph = uploadingPlaceholder(id, filename);
  const at = el && document.activeElement === el ? el.selectionStart : text.length;
  const before = text.slice(0, at);
  const after = text.slice(at);
  // Images render as blocks — keep the reference on its own line, with the
  // caret on a fresh line after it.
  const lead = before && !before.endsWith('\n') ? '\n' : '';
  const trail = after.startsWith('\n') ? '' : '\n';
  const inserted = lead + ph + trail;
  if (el) {
    el.setRangeText(inserted, at, at);
    el.setSelectionRange(at + inserted.length, at + inserted.length);
    return el.value;
  }
  return before + inserted + after;
}

/** Swap the upload placeholder for the final image reference, or drop it on
 *  a failed upload. No-op if the user already deleted the placeholder.
 *  Falls back to a plain string replace when no element is available. */
export function resolveUploadingPlaceholder(
  el: HTMLTextAreaElement | null,
  value: string,
  id: string,
  att: { filename: string; url: string } | null,
): string {
  const text = el ? el.value : value;
  const re = new RegExp(`!\\[[^\\]\\n]*\\]\\(uploading:${id}\\)`);
  const m = re.exec(text);
  if (!m) return text;
  const replacement = att ? inlineImageMarkdown(att) : '';
  const start = m.index;
  const end = start + m[0].length;
  if (!el) return text.slice(0, start) + replacement + text.slice(end);
  const selStart = el.selectionStart;
  const selEnd = el.selectionEnd;
  // Caret before the placeholder stays put, after it shifts with the length
  // change, inside it lands just past the swapped-in reference.
  const adjust = (p: number) =>
    p <= start ? p : p >= end ? p + replacement.length - m[0].length : start + replacement.length;
  el.setRangeText(replacement, start, end);
  el.setSelectionRange(adjust(selStart), adjust(selEnd));
  return el.value;
}

export type ContentSegment =
  | { type: 'text'; text: string }
  | { type: 'image'; alt: string; url: string };

/** Split message content into text runs and same-origin image references. */
export function parseInlineImages(content: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  let last = 0;
  for (const m of content.matchAll(INLINE_IMAGE_RE)) {
    if (m.index! > last) segments.push({ type: 'text', text: content.slice(last, m.index) });
    segments.push({ type: 'image', alt: m[1], url: m[2] });
    last = m.index! + m[0].length;
  }
  if (last < content.length) segments.push({ type: 'text', text: content.slice(last) });
  return segments;
}

/** Attachments not already shown inline in the content — what the chip list
 *  should display, so an inline screenshot isn't listed twice. */
export function unreferencedAttachments<T extends { url?: string | null }>(
  content: string,
  attachments: T[],
): T[] {
  return attachments.filter(a => !a.url || !content.includes(a.url));
}

const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp']);

/** True when an attachment is an image the lightbox can display. */
export function isImageAttachment(a: { filename?: string | null; mimeType?: string | null }): boolean {
  if (a.mimeType) return a.mimeType.startsWith('image/');
  const ext = (a.filename ?? '').split('.').pop()?.toLowerCase() ?? '';
  return IMAGE_EXT.has(ext);
}

/** One content block of a ticket page: a message (or the description) plus
 *  its attachment rows. */
export interface GalleryBlock {
  content: string;
  attachments: Array<{ filename?: string | null; url?: string | null; mimeType?: string | null }>;
}

/** Every image a ticket page displays, in display order and deduplicated:
 *  per block, inline image references first (that is how they render), then
 *  the image attachments shown as chips. Feeds the lightbox gallery so the
 *  arrows walk the whole ticket. */
export function collectGalleryImages(blocks: GalleryBlock[]): Array<{ url: string; name: string }> {
  const out: Array<{ url: string; name: string }> = [];
  const seen = new Set<string>();
  const push = (url: string, name: string) => {
    if (!seen.has(url)) { seen.add(url); out.push({ url, name }); }
  };
  for (const b of blocks) {
    for (const seg of parseInlineImages(b.content)) {
      if (seg.type === 'image') push(seg.url, seg.alt || 'image');
    }
    for (const a of unreferencedAttachments(b.content, b.attachments)) {
      if (a.url && isImageAttachment(a)) push(a.url, a.filename || 'image');
    }
  }
  return out;
}
